import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import {
  Notification,
  NotificationStatus,
  NotificationPriority,
} from '../../models/notification.model';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap, take, catchError } from 'rxjs/operators';
import {
  faArrowLeft,
  faCheck,
  faArchive,
  faClock,
  faExternalLinkAlt,
  faInfoCircle,
  faCalendarCheck,
  faFileSignature,
  faCalendarAlt,
  faTimes,
  faFileAlt,
} from '@fortawesome/free-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationReminderModalComponent } from '../notification-reminder-modal/notification-reminder-modal.component';
import { NotificationAction } from '../../models/notification.model';
import { AppToastService } from '../../../toast/app-toast.service';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';

@Component({
  selector: 'san-notification-detail',
  templateUrl: './notification-detail.component.html',
  styleUrls: ['./notification-detail.component.scss'],
  standalone: false,
})
export class NotificationDetailComponent implements OnInit {
  faArrowLeft = faArrowLeft;
  faCheck = faCheck;
  faArchive = faArchive;
  faClock = faClock;
  faCalendarAlt = faCalendarAlt;
  faExternalLink = faExternalLinkAlt;
  faTimes = faTimes;
  faFileAlt = faFileAlt;

  // Map for action icons
  actionIcons: Record<string, any> = {
    faCheck: faCheck,
    faTimes: faTimes,
    faFileAlt: faFileAlt,
  };

  icons: Record<string, IconDefinition> = {
    SYSTEM: faInfoCircle,
    APPOINTMENT: faCalendarCheck,
    MESSAGE: faFileSignature,
    S: faInfoCircle,
    A: faCalendarCheck,
    M: faFileSignature,
    DEFAULT: faInfoCircle,
  };

  notification$: Observable<Notification | undefined>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private modalService: NgbModal,
    private toastService: AppToastService,
  ) {
    this.notification$ = this.route.paramMap.pipe(
      map((params) => params.get('id')),
      switchMap((id) => {
        if (!id) return of(undefined);
        return this.notificationService.getNotification(id).pipe(
          catchError(() => {
            const cached = this.notificationService.getById(id);
            return of(cached);
          }),
        );
      }),
      tap((notification) => {
        if (notification && notification.status === NotificationStatus.UNREAD) {
          this.notificationService.markAsRead(notification.id).subscribe({
            next: () => {},
            error: () => {},
          });
        }
      }),
    );
  }

  ngOnInit(): void {}

  goBack(): void {
    this.router.navigate(['/notifications']);
  }

  markAsRead(id: string): void {
    this.notificationService.markAsRead(id).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  archive(id: string): void {
    this.notificationService.archive(id).subscribe({
      next: () => {
        this.goBack();
      },
      error: () => {
        this.goBack();
      },
    });
  }

  setReminder(id: string): void {
    this.notification$.pipe(take(1)).subscribe((notification) => {
      if (!notification) return;

      const modalRef = this.modalService.open(
        NotificationReminderModalComponent,
        { centered: true },
      );
      (
        modalRef.componentInstance as NotificationReminderModalComponent
      ).notificationTitle = notification.title;

      modalRef.result.then(
        (result) => {
          if (result instanceof Date) {
            this.notificationService.setReminder(id, result).subscribe({
              next: () => {},
              error: () => {},
            });
          }
        },
        () => {
          // dismissed
        },
      );
    });
  }

  handleAction(notification: Notification, action: NotificationAction): void {
    if (action.internalRoute) {
      this.toastService.show(
        'Aktion ausgeführt',
        `Aktion "${action.label}" wurde für "${notification.title}" aufgerufen.`,
        'success',
      );
    }
  }

  getIconForType(type: string): IconDefinition {
    return this.icons[type] || this.icons['DEFAULT'];
  }

  getStatusClass(status: NotificationStatus): string {
    switch (status) {
      case NotificationStatus.UNREAD:
        return 'bg-primary';
      case NotificationStatus.READ:
        return 'bg-secondary';
      case NotificationStatus.ARCHIVED:
        return 'bg-dark';
      default:
        return 'bg-light';
    }
  }

  getPriorityClass(priority: NotificationPriority): string {
    switch (priority) {
      case NotificationPriority.CRITICAL:
        return 'text-danger';
      case NotificationPriority.HIGH:
        return 'text-warning';
      case NotificationPriority.MEDIUM:
        return 'text-info';
      case NotificationPriority.LOW:
        return 'text-muted';
      default:
        return '';
    }
  }
}
